'use client';

import { CloseIcon } from '@otter-ai/den-icons';
import useMergedRef from '@react-hook/merged-ref';
import { cx } from 'cva';
import {
  type ElementRef,
  forwardRef,
  type ReactNode,
  useEffect,
  useRef,
} from 'react';
import {
  Dialog,
  DialogFooter,
  DialogHeader,
  DialogOverlay,
  type DialogProps,
} from '../../components/Dialog/Dialog';
import { IconButton } from '../../components/IconButton/IconButton';
import { Portal } from '../../components/Portal/PortalContext';
import { useFocusTrap } from '../../utils/hooks/useFocusTrap';
import { jsxTruthy } from '../../utils/jsx-truthy';

export type DialogßProps = {
  Header?: ReactNode;
  Footer?: ReactNode;
  /**
   * The react node to display in top right of the dialog header.
   * This is normally a close X button.
   * @default <IconButton variant="ghost" onClick={() => onOpenChange?.(!isOpen)}><CloseIcon /></IconButton>
   */
  action?: ReactNode;
  /**
   * The dialog body
   */
  children: ReactNode;
  /**
   * Additional class names to apply to the dialog container.
   */
  className?: string;
  description?: string;
  onOpenChange?: ((open: boolean) => void) | undefined;
  isOpen?: boolean;
  showOverlay?: boolean;
  /**
   * The dialog size.
   * @default 'md'
   */
  size?: DialogProps['size'];
  title?: React.ReactNode;
  'data-testid': string | null;
};

/**
 * A simple dialog component.
 * @beta
 * @example
 * <Dialogß title="Title" description="Description" isOpen={isOpen} onOpenChange={setIsOpen}>
 *  <div>Content</div>
 * </Dialogß>
 *
 * @param props.Header The dialog header.
 * @param props.Footer The dialog footer.
 * @param props.action The react node to display the X in top right of the dialog header.
 * @param props.children The dialog body
 * @param props.className Additional class names to apply to the dialog container.
 * @param props.description The dialog description.
 * @param props.onOpenChange The open change handler.
 * @param props.isOpen The dialog open state.
 * @param props.size The dialog size.
 * @param props.title The dialog title.
 * @param props.dataTestId The data-testid attribute to apply to the dialog container.
 */
export const Dialogß = forwardRef<ElementRef<'div'>, DialogßProps>(
  (props, ref) => {
    const {
      action,
      children,
      className,
      description,
      isOpen = true,
      onOpenChange,
      showOverlay = true,
      title,
      Footer,
      Header,
      'data-testid': dataTestId,
      ...dialogProps
    } = props;
    const testId = dataTestId ?? 'dialog';
    const focusTrapRef = useRef<HTMLDivElement>(null);
    const combinedRef = useMergedRef(focusTrapRef, ref);
    useFocusTrap(focusTrapRef);

    // esc key to close
    useEffect(() => {
      function handleKeyDown(event: KeyboardEvent) {
        if (event.key === 'Escape') {
          onOpenChange?.(false);
        }
      }

      if (isOpen) {
        document.addEventListener('keydown', handleKeyDown);
      }

      return () => {
        document.removeEventListener('keydown', handleKeyDown);
      };
    }, [isOpen, onOpenChange]);

    const ActionButton =
      action === undefined ? (
        <IconButton
          data-testid={`${testId}-close`}
          variant="ghost"
          onClick={() => {
            onOpenChange?.(!isOpen);
          }}
        >
          <CloseIcon />
        </IconButton>
      ) : (
        action
      );

    if (!isOpen) return null;
    return (
      <Portal>
        {showOverlay && (
          <DialogOverlay
            className="z-[1000]"
            onClick={() => {
              onOpenChange?.(false);
            }}
          />
        )}
        <Dialog
          data-testid={`${testId}-modal`}
          ref={combinedRef}
          {...dialogProps}
          className={cx('z-[1000]', className)}
        >
          {jsxTruthy(Header) ? (
            <>
              {Header}
              <div className="absolute top-6 right-6">{ActionButton}</div>
            </>
          ) : (
            <DialogHeader
              data-testid={testId}
              title={title}
              description={description}
              action={ActionButton}
            />
          )}
          {children}
          {jsxTruthy(Footer) && (
            <DialogFooter data-testid={`${testId}-footer`}>
              {Footer}
            </DialogFooter>
          )}
        </Dialog>
      </Portal>
    );
  },
);
